import { useMemo } from "react";
import Icons from "assets";
import { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { useFeatureFlag } from "contexts/feature-flag-context";
import { isTouchpointInReview } from "utilities/touchpoint";
import { getActionTouchpointTypeLabel } from "utilities";
import { useTouchpointActionContext } from "../contexts/touchpoint-action-context";
import { TouchpointDTOBatchJobExecutionStatus } from "generated/schemas";
import { useRetrySyncTouchpoint } from "hooks/api/use-touchpoints";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useEmailWriterPollingSessionActive } from "hooks/use-email-writer-polling-session-active";
import { getBatchJobBadgeVariant } from "utilities/sync-status";
import { hasCampaignTemplateInstance } from "containers/admin/clients/campaign/campaign-template-instance.utils";
import { TouchpointStatuses } from "types";

// The campaign rewrite endpoint only queues emails whose latest version is still a draft.
const REWRITABLE_STATUSES: ReadonlySet<string> = new Set([
  TouchpointStatuses.DRAFT,
  TouchpointStatuses.SYSTEM_DRAFT,
]);

export interface TouchpointActionItem {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "error";
}

export type TouchpointMenuItem = TouchpointActionItem | { type: "divider" };

export const useTouchpointActions = (touchpoint: TouchpointAttributes) => {
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const isProgramBriefsEnabled = useFeatureFlag("program-briefs");
  const currentCampaign = useCurrentCampaign();
  const isEmailWriterSessionActive = useEmailWriterPollingSessionActive(
    touchpoint.campaignId,
    touchpoint.clientId,
  );
  const batchJobVariant = getBatchJobBadgeVariant(
    touchpoint,
    hasCampaignTemplateInstance(currentCampaign),
    { isEmailWriterSessionActive },
  );
  const canRetryWriting =
    isProgramBriefsEnabled && REWRITABLE_STATUSES.has(touchpoint.latestVersion?.status ?? "");
  const context = useTouchpointActionContext();
  const retrySyncMutation = useRetrySyncTouchpoint();

  const actions = useMemo((): TouchpointMenuItem[] => {
    const {
      openCloneModal,
      openArchiveModal,
      openAddToFolderModal,
      openAddBatchModal,
      openRollToNextFYModal,
      openMoveToCampaignModal,
      openRewriteEmailModal,
    } = context;

    const touchpointTypeLabel = getActionTouchpointTypeLabel(touchpoint.type).toLowerCase();

    const items: TouchpointMenuItem[] = [
      {
        label: "Roll to next FY",
        icon: <Icons.Edit />,
        onClick: () => openRollToNextFYModal(touchpoint),
      },
      {
        label: `Clone ${touchpointTypeLabel}`,
        icon: <Icons.Clone />,
        onClick: () => openCloneModal(touchpoint),
      },
      { type: "divider" },
    ];

    const isJobFailed =
      touchpoint.batchJobExecutionStatus === TouchpointDTOBatchJobExecutionStatus.FAILED;

    // A failed writing job gets no Monday retry even when it cannot be rewritten here: that
    // retry restarts the failed execution, which is the writing job, not a Monday sync.
    if (isJobFailed && batchJobVariant === "email-writer" && canRetryWriting) {
      items.unshift({
        label: "Retry writing",
        icon: <Icons.Refresh />,
        onClick: () => openRewriteEmailModal(touchpoint),
      });
    }

    if (isJobFailed && batchJobVariant === "monday") {
      items.unshift({
        label: "Retry sync with Monday",
        icon: <Icons.Refresh />,
        onClick: () => retrySyncMutation.mutate(touchpoint.id),
      });
    }

    if (touchpoint.folderId) {
      items.push({
        label: "Move to folder",
        icon: <Icons.Folder />,
        onClick: () => openAddToFolderModal(touchpoint),
      });
    } else {
      items.push({
        label: "Add to folder",
        icon: <Icons.Folder />,
        onClick: () => openAddToFolderModal(touchpoint),
      });
    }

    if (isBatchManagementEnabled && isTouchpointInReview(touchpoint)) {
      const isTouchpointInBatch = !!touchpoint.touchpointBatchId;
      items.push({
        label: isTouchpointInBatch ? "Change batch" : "Add to batch",
        icon: <Icons.Layers />,
        onClick: () => openAddBatchModal(touchpoint),
      });
    }

    items.push({
      label: "Move to campaign",
      icon: <Icons.SwapHorizontal />,
      onClick: () => openMoveToCampaignModal(touchpoint),
    });

    items.push({
      label: `Archive ${touchpointTypeLabel}`,
      icon: <Icons.Archive />,
      onClick: () => openArchiveModal(touchpoint),
    });

    return items;
  }, [context, touchpoint, batchJobVariant, canRetryWriting, isBatchManagementEnabled]);

  return {
    actions,
  };
};
